import React, { useEffect, useState } from "react";
import { useNotification } from "../../hooks/useNotification";
import { getStoredApiKey } from "../../hooks/useAnnouncement";
import {
  fetchOffers,
  saveOffer,
  deleteOffer,
  fetchOfferEvents,
  fetchRewardsForOffer,
  markRewarded,
  unmarkRewarded,
} from "../../hooks/useOffers";
import { PACKAGES } from "../../config/constants";
import Button from "../../components/Common/Button";
import { Card } from "../../components/Common/Card";

// Convert <input type="datetime-local"> value → unix seconds
function toUnix(dtLocal) {
  if (!dtLocal) return 0;
  return Math.floor(new Date(dtLocal).getTime() / 1000);
}
function fromUnix(ts) {
  if (!ts) return "";
  const d = new Date(ts * 1000);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function AdminRewards() {
  const notify = useNotification();
  const keyPresent = !!getStoredApiKey();

  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOfferId, setSelectedOfferId] = useState(0);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null); // offer being edited, null = new

  const reloadOffers = async () => {
    setLoading(true);
    const o = await fetchOffers();
    setOffers(o);
    setLoading(false);
  };
  useEffect(() => { reloadOffers(); }, []);

  const openNew = () => {
    const now = new Date();
    const in7days = new Date(now.getTime() + 7 * 86400 * 1000);
    setEditing({
      id: 0,
      name: "",
      startTs: Math.floor(now.getTime() / 1000),
      endTs: Math.floor(in7days.getTime() / 1000),
      rewardLabel: "",
    });
    setShowEditor(true);
  };
  const openEdit = (o) => {
    setEditing({ ...o });
    setShowEditor(true);
  };
  const saveCurrent = async () => {
    const res = await saveOffer(editing);
    if (res.ok) {
      notify.success(editing.id ? "Offer updated" : "Offer created");
      setShowEditor(false);
      reloadOffers();
    } else {
      notify.error(res.error || "Save failed");
    }
  };
  const removeOffer = async (id) => {
    if (!window.confirm("Delete this offer? Qualifying-user records stay in the database.")) return;
    const res = await deleteOffer(id);
    if (res.ok) {
      notify.success("Offer deleted");
      if (selectedOfferId === id) setSelectedOfferId(0);
      reloadOffers();
    } else {
      notify.error(res.error || "Delete failed");
    }
  };

  if (!keyPresent) {
    return (
      <Card>
        <div style={{ color: "var(--warning)", fontWeight: 700, marginBottom: 6 }}>⚠ Admin API Key Not Set</div>
        <div style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.5 }}>
          Open the <b>News</b> tab and paste your key before using Rewards.
        </div>
      </Card>
    );
  }

  const now = Math.floor(Date.now() / 1000);

  return (
    <div>
      <div className="section-header" style={{ marginTop: 0 }}>
        <div className="section-title">Reward Offers</div>
        <Button variant="primary" size="sm" onClick={openNew}>+ New Offer</Button>
      </div>

      {loading ? (
        <Card><div className="empty-state-text"><span className="spinner" /> Loading…</div></Card>
      ) : offers.length === 0 ? (
        <Card>
          <div className="empty-state-text">
            No offers yet
            <div style={{ marginTop: 8, fontSize: 11, color: "var(--text-muted)" }}>
              Click <b>+ New Offer</b> to create one. User activations, re-topups, and registrations
              during the offer window will be auto-recorded.
            </div>
          </div>
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
          {offers.map((o) => {
            const active = o.startTs <= now && now <= o.endTs;
            const future = o.startTs > now;
            const label = active ? "ACTIVE" : future ? "SCHEDULED" : "ENDED";
            const color = active ? "var(--success)" : future ? "var(--violet-bright)" : "var(--text-muted)";
            const isOpen = selectedOfferId === o.id;
            return (
              <div
                key={o.id}
                className={`card ${active ? "card-glow" : ""}`}
                style={{ cursor: "pointer" }}
                onClick={() => setSelectedOfferId(isOpen ? 0 : o.id)}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <span className="badge" style={{
                    background: color + "22",
                    color,
                    border: `1px solid ${color}66`,
                    fontSize: 9, fontWeight: 700, letterSpacing: "0.08em",
                  }}>{label}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 14 }}>
                      {o.name}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>
                      {new Date(o.startTs * 1000).toLocaleString("en-US", { timeZone: "UTC" })} →{" "}
                      {new Date(o.endTs * 1000).toLocaleString("en-US", { timeZone: "UTC" })} UTC
                    </div>
                    <div style={{ fontSize: 11, color: "var(--cyan)", marginTop: 2 }}>
                      🎁 {o.rewardLabel}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(o)}>Edit</Button>
                    <Button variant="ghost" size="sm" onClick={() => removeOffer(o.id)}>×</Button>
                  </div>
                </div>
                {isOpen && <OfferDetail offer={o} />}
              </div>
            );
          })}
        </div>
      )}

      {showEditor && (
        <OfferEditor
          editing={editing}
          onChange={setEditing}
          onClose={() => setShowEditor(false)}
          onSave={saveCurrent}
          toUnix={toUnix}
          fromUnix={fromUnix}
        />
      )}
    </div>
  );
}

// ============================================================
// Offer detail — qualifying users + reward distribution
// ============================================================
function OfferDetail({ offer }) {
  const notify = useNotification();
  const [events, setEvents] = useState([]);
  const [rewarded, setRewarded] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [paidTxHash, setPaidTxHash] = useState("");
  const [note, setNote] = useState("");

  const reload = async () => {
    setLoading(true);
    const [e, r] = await Promise.all([
      fetchOfferEvents(offer.id, { dedupe: true }),
      fetchRewardsForOffer(offer.id),
    ]);
    setEvents(e);
    setRewarded(r);
    setLoading(false);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [offer.id]);

  // Build a set of already-paid wallets for quick lookup
  const paidWallets = new Set(rewarded.map((r) => (r.wallet || "").toLowerCase()));

  // Group events by wallet so each user shows once with their actions summarized
  const byWallet = new Map();
  for (const e of events) {
    const w = (e.wallet || "").toLowerCase();
    if (!w) continue;
    if (!byWallet.has(w)) byWallet.set(w, { wallet: w, userId: e.userId, name: e.name, actions: [] });
    byWallet.get(w).actions.push(e);
  }
  const rows = Array.from(byWallet.values()).sort((a, b) => {
    const aLatest = Math.max(...a.actions.map((x) => x.ts || 0));
    const bLatest = Math.max(...b.actions.map((x) => x.ts || 0));
    return bLatest - aLatest;
  });

  const toggleRow = (wallet) => {
    const copy = new Set(selected);
    if (copy.has(wallet)) copy.delete(wallet); else copy.add(wallet);
    setSelected(copy);
  };
  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.wallet)));
  };

  const markSelectedAsRewarded = async () => {
    if (selected.size === 0) return notify.error("Select at least one user");
    let ok = 0, failed = 0;
    for (const wallet of selected) {
      const row = rows.find((r) => r.wallet === wallet);
      const res = await markRewarded({
        offerId: offer.id,
        wallet,
        userId: row?.userId || "",
        paidTxHash,
        amount: offer.rewardLabel,
        note,
      });
      if (res.ok) ok++; else failed++;
    }
    notify.success(`${ok} marked rewarded${failed > 0 ? `, ${failed} failed` : ""}`);
    setSelected(new Set());
    setPaidTxHash("");
    setNote("");
    reload();
  };

  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text); notify.success("Copied"); }
    catch { notify.error("Could not copy"); }
  };

  const exportCsv = () => {
    const headers = ["userId", "name", "wallet", "action", "packageId", "amount", "ts", "txHash", "rewarded"];
    const lines = [headers.join(",")];
    for (const row of rows) {
      for (const a of row.actions) {
        const r = [
          row.userId,
          `"${(row.name || "").replace(/"/g, '""')}"`,
          row.wallet,
          a.action,
          a.packageId,
          a.amount,
          a.ts ? new Date(a.ts * 1000).toISOString() : "",
          a.txHash,
          paidWallets.has(row.wallet) ? "Y" : "N",
        ].join(",");
        lines.push(r);
      }
    }
    const csv = "﻿" + lines.join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `offer-${offer.id}-${offer.name.replace(/\s+/g, "_")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border)" }} onClick={(e) => e.stopPropagation()}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
          <b>{rows.length}</b> qualifying users · <b>{rewarded.length}</b> already rewarded
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <Button variant="ghost" size="sm" onClick={reload}>↻ Refresh</Button>
          <Button variant="ghost" size="sm" onClick={exportCsv} disabled={rows.length === 0}>Export CSV</Button>
        </div>
      </div>

      {loading ? (
        <div className="empty-state-text"><span className="spinner" /></div>
      ) : rows.length === 0 ? (
        <div className="empty-state-text">
          No qualifying users yet.
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>
            Users who register, activate, or re-topup during this window will appear here.
          </div>
        </div>
      ) : (
        <>
          {/* Bulk mark controls */}
          <div style={{
            background: "rgba(0,229,255,0.05)",
            border: "1px solid var(--cyan-border)",
            borderRadius: 10,
            padding: 10,
            marginBottom: 10,
          }}>
            <div style={{ fontSize: 11, color: "var(--text-secondary)", marginBottom: 8 }}>
              Select users below → paste payout tx hash (optional) + note (optional) → click Mark Rewarded.
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <input
                className="input"
                placeholder="Payout tx hash (optional)"
                value={paidTxHash}
                onChange={(e) => setPaidTxHash(e.target.value)}
                style={{ flex: "1 1 200px", fontSize: 11, fontFamily: "var(--font-mono)" }}
              />
              <input
                className="input"
                placeholder="Note (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                style={{ flex: "1 1 180px", fontSize: 11 }}
              />
              <Button
                variant="primary"
                size="sm"
                onClick={markSelectedAsRewarded}
                disabled={selected.size === 0}
              >
                Mark {selected.size || ""} Rewarded
              </Button>
            </div>
          </div>

          <div style={{ fontSize: 10, color: "var(--text-muted)", marginBottom: 6, display: "flex", justifyContent: "space-between" }}>
            <span>
              <input type="checkbox" checked={selected.size === rows.length && rows.length > 0} onChange={toggleAll} />
              {" "}Select all
            </span>
            <span>Click row to view actions</span>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {rows.map((row) => {
              const already = paidWallets.has(row.wallet);
              const isSel = selected.has(row.wallet);
              return (
                <div
                  key={row.wallet}
                  style={{
                    background: already ? "rgba(251,191,36,0.06)" : isSel ? "rgba(0,229,255,0.06)" : "#050815",
                    border: `1px solid ${already ? "rgba(251,191,36,0.4)" : isSel ? "var(--cyan-border)" : "var(--border-strong)"}`,
                    borderRadius: 10,
                    padding: 10,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={isSel}
                      disabled={already}
                      onChange={() => toggleRow(row.wallet)}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
                          {row.name || "—"}
                        </span>
                        {row.userId && <span className="badge badge-cyan" style={{ fontSize: 9 }}>#{row.userId}</span>}
                        {already && (
                          <span className="badge" style={{
                            background: "rgba(251,191,36,0.15)",
                            color: "#fbbf24",
                            border: "1px solid rgba(251,191,36,0.4)",
                            fontSize: 9, fontWeight: 700,
                          }}>🏆 REWARDED</span>
                        )}
                      </div>
                      <div style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 10,
                        color: "var(--cyan)",
                        marginTop: 2,
                        display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
                      }}>
                        <span>{row.wallet}</span>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); copyText(row.wallet); }}
                          style={{
                            background: "rgba(0,229,255,0.1)",
                            border: "1px solid var(--cyan-border)",
                            color: "var(--cyan)",
                            padding: "1px 6px",
                            borderRadius: 4,
                            fontSize: 9,
                            cursor: "pointer",
                          }}
                        >Copy</button>
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: 8, display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {row.actions.map((a, i) => {
                      const pkg = a.packageId ? PACKAGES[a.packageId - 1] : null;
                      const bg = a.action === "register" ? "#00e5ff" : a.action === "activate" ? "#8b5cf6" : "#ec4899";
                      return (
                        <span
                          key={i}
                          style={{
                            fontSize: 9,
                            padding: "2px 7px",
                            borderRadius: 10,
                            background: bg + "22",
                            color: bg,
                            border: `1px solid ${bg}55`,
                            fontWeight: 700,
                            letterSpacing: "0.04em",
                            textTransform: "uppercase",
                          }}
                          title={a.ts ? new Date(a.ts * 1000).toLocaleString() : ""}
                        >
                          {a.action}{pkg ? ` P${pkg.id}` : ""}
                          {a.amount ? ` $${a.amount}` : ""}
                        </span>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================
// Offer editor modal (inline)
// ============================================================
function OfferEditor({ editing, onChange, onClose, onSave, toUnix, fromUnix }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.8)", backdropFilter: "blur(6px)",
        zIndex: 9999,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0a0e24",
          border: "1px solid var(--cyan-border)",
          borderRadius: 14,
          padding: 18,
          width: "100%",
          maxWidth: 420,
        }}
      >
        <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, marginBottom: 14 }}>
          {editing.id ? "Edit Offer" : "New Reward Offer"}
        </div>

        <div className="input-group">
          <label className="input-label">Offer name</label>
          <input
            className="input"
            placeholder="e.g. October Activation Bonus"
            value={editing.name}
            onChange={(e) => onChange({ ...editing, name: e.target.value })}
            maxLength={100}
          />
        </div>

        <div className="input-group">
          <label className="input-label">Reward label (shown to users)</label>
          <input
            className="input"
            placeholder="e.g. 50 USDT bonus"
            value={editing.rewardLabel}
            onChange={(e) => onChange({ ...editing, rewardLabel: e.target.value })}
            maxLength={100}
          />
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <div className="input-group" style={{ flex: 1 }}>
            <label className="input-label">Start</label>
            <input
              type="datetime-local"
              className="input"
              value={fromUnix(editing.startTs)}
              onChange={(e) => onChange({ ...editing, startTs: toUnix(e.target.value) })}
            />
          </div>
          <div className="input-group" style={{ flex: 1 }}>
            <label className="input-label">End</label>
            <input
              type="datetime-local"
              className="input"
              value={fromUnix(editing.endTs)}
              onChange={(e) => onChange({ ...editing, endTs: toUnix(e.target.value) })}
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <Button variant="ghost" full onClick={onClose}>Cancel</Button>
          <Button variant="primary" full onClick={onSave}>Save</Button>
        </div>
      </div>
    </div>
  );
}

export default AdminRewards;
